import React, { useState, useEffect, useCallback } from 'react';
import researchService, { ResearchType } from '../services/researchService';
import gtmService, { Icp, Persona } from '../services/gtmService';
import EditableMarkdown from '../components/EditableMarkdown';
import SavedAiDocuments from '../components/SavedAiDocuments';
import aiDocumentService from '../services/aiDocumentService';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const TYPES: { value: ResearchType; label: string; placeholder: string }[] = [
  { value: 'competitor', label: 'Wettbewerbsanalyse', placeholder: 'Firma/Produkt, das analysiert werden soll' },
  { value: 'market_edp', label: 'Marktforschung (EDPs)', placeholder: 'SaaS-Kategorie / Vertical, z.B. „Cold-Outreach-Tools"' },
  { value: 'niche_data', label: 'Datenquellen-Scout', placeholder: 'Vertical / Zielmarkt, z.B. „DACH-FinTechs"' },
  { value: 'pain', label: 'Pain-Point-Analyse', placeholder: 'Zielunternehmen (Name + ggf. Kontext)' },
  { value: 'trigger', label: 'Buying-Trigger-Analyse', placeholder: 'ICP (strategisch) ODER konkrete Firma/Signal (taktisch)' },
  { value: 'cold_call', label: 'Cold-Call-Skript', placeholder: 'Ziel: Titel/Branche/Trigger + dein Angebot in 1 Satz' },
  { value: 'deep_company', label: 'Deep-Company-Analyse', placeholder: 'Zielunternehmen (Name + Website/Kontext)' },
  { value: 'gtm_thinker', label: 'GTM-Action-Thinker', placeholder: 'Deine GTM-Idee / Angle / Plan zum Challengen' },
];

const ResearchHubPage: React.FC = () => {
  const [type, setType] = useState<ResearchType>('competitor');
  const [input, setInput] = useState('');
  const [icpId, setIcpId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [documentId, setDocumentId] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const loadGtm = useCallback(async () => {
    try {
      const [i, p] = await Promise.all([gtmService.getIcps(), gtmService.getPersonas()]);
      setIcps(i);
      setPersonas(p);
    } catch {
      // optional
    }
  }, []);

  useEffect(() => {
    loadGtm();
  }, [loadGtm]);

  const placeholder = TYPES.find((t) => t.value === type)?.placeholder || '';

  const handleRun = async () => {
    if (!input.trim()) {
      setError('Bitte ein Ziel / eine Beschreibung angeben');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setContent('');
      const result = await researchService.run({
        type,
        input,
        icpId: icpId || undefined,
        personaId: personaId || undefined,
      });
      setContent(result.content);
      setTitle(result.title);
      setDocumentId(result.documentId);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Recherche');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <PageHeader title="Marktrecherche (Sales Research)" subtitle={routeById('research')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Gebündelte GTM-Recherche-Skills: Wettbewerb, Markt-EDPs, Datenquellen, Pain Points,
        Buying-Trigger und Cold-Call-Skripte — optional auf ICP/Persona aus dem GTM-Setup geerdet.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div className="form-group">
          <label className="form-label">Recherche-Typ</label>
          <select className="form-input" value={type} onChange={(e) => setType(e.target.value as ResearchType)}>
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Ziel / Beschreibung</label>
          <textarea
            className="form-textarea"
            rows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">ICP (GTM, optional)</label>
            <select className="form-input" value={icpId} onChange={(e) => setIcpId(e.target.value)}>
              <option value="">— keiner —</option>
              {icps.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Persona (GTM, optional)</label>
            <select className="form-input" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— keine —</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn btn-success" onClick={handleRun} disabled={loading}>
          {loading ? 'Recherchiere…' : 'Report erstellen'}
        </button>
      </div>

      {content && (
        <EditableMarkdown
          title={title}
          content={content}
          onSave={
            documentId
              ? async (c) => {
                  await aiDocumentService.update(documentId, { content: c });
                  setContent(c);
                  setReloadKey((k) => k + 1);
                }
              : undefined
          }
        />
      )}

      <SavedAiDocuments feature="research" reloadKey={reloadKey} />
    </div>
  );
};

export default ResearchHubPage;
